// VpnCloudWatchPanel.jsx - "AWS VPN tunnels (CloudWatch)" card on Network Topology -> Tunnels & WAN.
// Data from backend/vpnCwMetrics.js (GET /api/net/vpn-cw/summary, POST /api/net/vpn-cw/fetch).
// Per tunnel: official AWS TunnelState (up/down) and TunnelDataIn/Out, 5-minute points aggregated per hour.
import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Download } from 'lucide-react';
import { api } from '../context/AuthContext';

const RANGES = [[1, '24h'], [7, '7d'], [30, '30d']];
const C_UP = '#10b981', C_DOWN = '#e11d48', C_NONE = '#e2e8f0', C_IN = '#2563eb', C_OUT = '#f59e0b';

const fmtBytes = (n) => {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB']; let i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n >= 100 || i === 0 ? 0 : 1)} ${u[i]}`;
};
// Timestamps from the API are UTC 'YYYY-MM-DD HH:MM:SS'; show them in WIB.
const wib = (s) => {
  if (!s) return '-';
  const d = new Date(Date.parse(s.replace(' ', 'T') + 'Z') + 7 * 3600e3);
  return d.toISOString().slice(0, 16).replace('T', ' ');
};
const fmtDur = (m) => (m < 60 ? `${m} min` : m < 1440 ? `${(m / 60).toFixed(1)} h` : `${(m / 1440).toFixed(1)} d`);
const pctColor = (v) => (v == null ? '#94a3b8' : v >= 99.9 ? '#059669' : v >= 99 ? '#d97706' : '#e11d48');

// One block per hour: green = up the whole hour, red = down at least once (TunnelState min < 1).
function StateStrip({ hourly }) {
  const W = 760, H = 14;
  const n = Math.max(hourly.length, 1), w = W / n;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height: H, display: 'block' }}>
      {hourly.map((h, i) => (
        <rect key={h.t} x={i * w} y={0} width={Math.max(w - 0.5, 0.5)} height={H}
          fill={h.state == null ? C_NONE : h.state >= 1 ? C_UP : C_DOWN}>
          <title>{`${wib(h.t)} WIB · ${h.state == null ? 'no data' : h.state >= 1 ? 'up' : 'DOWN'}`}</title>
        </rect>
      ))}
    </svg>
  );
}

// Traffic per hour: into AWS above the axis, from AWS below it.
function TrafficChart({ hourly }) {
  const W = 760, H = 90, mid = H / 2;
  const max = Math.max(1, ...hourly.map((h) => Math.max(h.bin, h.bout)));
  const n = Math.max(hourly.length, 1), w = W / n;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height: H, display: 'block' }}>
      <line x1={0} x2={W} y1={mid} y2={mid} stroke="#cbd5e1" strokeWidth={0.5} />
      {hourly.map((h, i) => {
        const a = (h.bin / max) * (mid - 2), b = (h.bout / max) * (mid - 2);
        return (
          <g key={h.t}>
            <rect x={i * w} y={mid - a} width={Math.max(w - 0.5, 0.5)} height={a} fill={C_IN} opacity={0.85} />
            <rect x={i * w} y={mid} width={Math.max(w - 0.5, 0.5)} height={b} fill={C_OUT} opacity={0.85} />
            <title>{`${wib(h.t)} WIB · in ${fmtBytes(h.bin)} · out ${fmtBytes(h.bout)}`}</title>
          </g>
        );
      })}
    </svg>
  );
}

export default function VpnCloudWatchPanel() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [acct, setAcct] = useState('all');

  const load = useCallback(async () => {
    setBusy('load'); setErr('');
    try { const r = await api.get(`/api/net/vpn-cw/summary?days=${days}`); setData(r.data); }
    catch (e) { setErr(e.response?.data?.error || e.message); }
    finally { setBusy(''); }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const fetchNow = async () => {
    setBusy('fetch'); setErr('');
    try { await api.post('/api/net/vpn-cw/fetch', { hours: 3 }); await load(); }
    catch (e) { setErr(e.response?.data?.error || e.message); setBusy(''); }
  };

  // Group tunnels by VPN connection. Tunnels that were never up in the range (unused backup
  // tunnels) are not drawn as cards; they are listed compactly at the bottom instead.
  const accounts = [...new Set((data?.tunnels || []).map((t) => t.account))].sort();
  const shown = (data?.tunnels || []).filter((t) => acct === 'all' || t.account === acct);
  const inactive = shown.filter((t) => !t.availability);
  const groups = {};
  shown.filter((t) => t.availability).forEach((t) => { const k = `${t.account} · ${t.vpnName}`; (groups[k] = groups[k] || []).push(t); });

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex flex-wrap items-center gap-3 mb-1">
        <div>
          <div className="font-semibold text-slate-800">AWS VPN tunnels · CloudWatch</div>
          <div className="text-xs text-slate-500">
            Official AWS tunnel state (TunnelState) and traffic (TunnelDataIn/Out) per tunnel · PIL &amp; Indomas accounts · 5-minute data, shown per hour in WIB · down reasons from Site-to-Site VPN tunnel logs
          </div>
        </div>
        <div className="ml-auto flex items-center gap-1">
          {accounts.length > 1 && ['all', ...accounts].map((a) => (
            <button key={a} onClick={() => setAcct(a)}
              className={`px-2.5 py-1 rounded-md text-xs font-semibold ${acct === a ? 'bg-teal-700 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>{a === 'all' ? 'All accounts' : a}</button>
          ))}
          {accounts.length > 1 && <span className="w-px h-5 bg-slate-200 mx-1" />}
          {RANGES.map(([d, l]) => (
            <button key={d} onClick={() => setDays(d)}
              className={`px-2.5 py-1 rounded-md text-xs font-semibold ${days === d ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>{l}</button>
          ))}
          <button onClick={load} disabled={!!busy} title="Reload from database"
            className="ml-1 p-1.5 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-600 disabled:opacity-50">
            <RefreshCw size={14} className={busy === 'load' ? 'animate-spin' : ''} />
          </button>
          <button onClick={fetchNow} disabled={!!busy} title="Pull the last 3 hours from CloudWatch now"
            className="p-1.5 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-600 disabled:opacity-50">
            <Download size={14} className={busy === 'fetch' ? 'animate-pulse' : ''} />
          </button>
        </div>
      </div>

      {err && <div className="text-sm text-rose-600 my-2">{err}</div>}
      {!data && !err && <div className="text-sm text-slate-500 my-3">Loading…</div>}
      {data && !data.tunnels.length && (
        <div className="text-sm text-slate-500 my-3">No CloudWatch data yet. It is pulled by cron <code>aws_vpn_cw_metrics</code> every hour; press the download button to pull now.</div>
      )}

      {Object.entries(groups).map(([g, ts]) => (
        <div key={g} className="mt-3">
          <div className="text-sm font-semibold text-slate-700 mb-1">{g} <span className="text-xs font-normal text-slate-400">{ts[0].vpnId}</span></div>
          {ts.map((t) => (
            <div key={t.ip} className="border border-slate-200 rounded-lg p-3 mb-2">
              <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm mb-2">
                <span className="font-mono font-semibold text-slate-800">{t.ip}</span>
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold"
                  style={{ color: t.current === 'up' ? '#059669' : t.current === 'down' ? '#e11d48' : '#64748b' }}>
                  <span className="w-2 h-2 rounded-full" style={{ background: t.current === 'up' ? C_UP : t.current === 'down' ? C_DOWN : '#94a3b8' }} />
                  {t.current ? t.current.toUpperCase() : 'n/a'}
                </span>
                <span className="text-xs text-slate-500">Availability <b style={{ color: pctColor(t.availability) }}>{t.availability == null ? '—' : `${t.availability.toFixed(2)}%`}</b></span>
                <span className="text-xs text-slate-500">Outages <b className="text-slate-700">{t.outages.length}</b></span>
                <span className="text-xs text-slate-500">Into AWS <b style={{ color: C_IN }}>{fmtBytes(t.bytesIn)}</b></span>
                <span className="text-xs text-slate-500">From AWS <b style={{ color: C_OUT }}>{fmtBytes(t.bytesOut)}</b></span>
                <span className="text-xs text-slate-400 ml-auto">last point {wib(t.lastTs)} WIB</span>
              </div>
              <StateStrip hourly={t.hourly} />
              <div className="mt-1.5"><TrafficChart hourly={t.hourly} /></div>
              <div className="flex justify-between text-[11px] text-slate-400 mt-0.5">
                <span>{t.hourly.length ? wib(t.hourly[0].t) : ''}</span>
                <span><span style={{ color: C_IN }}>■</span> into AWS (up) · <span style={{ color: C_OUT }}>■</span> from AWS (down)</span>
                <span>{t.hourly.length ? wib(t.hourly[t.hourly.length - 1].t) : ''}</span>
              </div>
              {t.outages.length > 0 && (
                <details className="mt-2">
                  <summary className="text-xs text-slate-600 cursor-pointer">Outage history ({t.outages.length})</summary>
                  <table className="w-full text-xs mt-1">
                    <thead><tr className="text-slate-500 text-left"><th className="py-1">Down from (WIB)</th><th>Recovered (WIB)</th><th className="text-right">Duration</th><th className="pl-3">Reason (AWS tunnel log)</th></tr></thead>
                    <tbody>
                      {t.outages.slice(0, 50).map((o) => (
                        <tr key={o.start} className="border-t border-slate-100">
                          <td className="py-1">{wib(o.start)}</td>
                          <td>{o.ongoing ? <b className="text-rose-600">ongoing</b> : wib(o.end)}</td>
                          <td className="text-right">{fmtDur(o.minutes)}</td>
                          <td className="pl-3 text-slate-600">{o.reason || <span className="text-slate-400">no tunnel log in this period</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              )}
            </div>
          ))}
        </div>
      ))}
      {inactive.length > 0 && (
        <div className="mt-3 text-xs text-slate-500 border border-dashed border-slate-200 rounded-lg p-2.5">
          <b className="text-slate-600">Never up in this range ({inactive.length})</b> — configured tunnels with no UP sample, usually the unused second tunnel of a VPN:{' '}
          {inactive.map((t) => `${t.account} · ${t.vpnName} · ${t.ip}`).join(', ')}.
        </div>
      )}
      {data && data.tunnels.length > 0 && (
        <div className="text-[11px] text-slate-400 mt-2">
          Strip: one block per hour — <span style={{ color: C_UP }}>■</span> up, <span style={{ color: C_DOWN }}>■</span> down at least once, <span style={{ color: '#cbd5e1' }}>■</span> no data.
          Source tables <code>aws_vpn_tunnel_metric</code> and <code>aws_vpn_tunnel_log</code> (Site-to-Site VPN tunnel logs), cron <code>aws_vpn_cw_metrics</code> (hourly).
        </div>
      )}
    </div>
  );
}
